import { RefreshCw, Trash2, TriangleAlert } from 'lucide-react';
import { observer } from 'mobx-react';
import React from 'react';
import { Link } from 'react-router-dom';
import { useWrapRequest } from 'use-wrap-request';
import { PageHeader } from '@components/page-header';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { toast } from '@core/toast';
import { ConfirmationModal } from '../../components/confirmation-modal';
import { Filters } from '../../components/filters';
import { userSearchDebounce } from '../../components/hooks/use-search';
import { useSearchReducer } from '../../components/hooks/useSearchReducer';
import { CellProps, TableComponent } from '../../components/table';
import { Api } from '../../core/api';
import {
    ProductOrderState,
    ThirdPartyPurchaseSupportDto,
    VendorSource
} from '../../core/api/WalletClient';
import { I18n } from '../../core/i18n';
import { links } from '../../core/router';
import { injectTSDI } from '../../core/tsdi';
import { BulkSyncModal } from './bulk-sync-modal';

type StatusFilter = ProductOrderState | 'ALL';
type SourceFilter = VendorSource | 'ALL';
type ConfirmActionType = 'retry' | 'forceRetry' | 'delete';

interface ConfirmActionState {
    action: ConfirmActionType;
    purchaseId: number;
}

function getErrorMessage(error: any): string {
    if (error && typeof error === 'object' && 'error' in error) {
        const errorObject = error.error;
        if (errorObject?.message?.defaultMessage) {
            return errorObject.message.defaultMessage;
        }
        if (errorObject?.defaultMessage) {
            return errorObject.defaultMessage;
        }
    }
    if (error instanceof Error) {
        return error.message;
    }
    return 'Unexpected error';
}

function getThirdPartyType(row: ThirdPartyPurchaseSupportDto): string {
    const value = (row as unknown as Record<string, unknown>).thirdPartyType;
    if (typeof value === 'string' && value.trim().length > 0) {
        return value;
    }
    return 'VOUCHER';
}

function getOrderErrorMessage(
    row: ThirdPartyPurchaseSupportDto
): string | undefined {
    const record = row as unknown as Record<string, unknown>;
    const orderErrorMessage = record.orderErrorMessage;
    if (
        typeof orderErrorMessage === 'string' &&
        orderErrorMessage.trim().length > 0
    ) {
        return orderErrorMessage;
    }

    const errorMessage = record.errorMessage;
    if (typeof errorMessage === 'string' && errorMessage.trim().length > 0) {
        return errorMessage;
    }

    return undefined;
}

function getOrderStatusVariant(status: ProductOrderState) {
    switch (status) {
        case ProductOrderState.ERROR:
            return 'destructive';
        case ProductOrderState.PENDING:
            return 'info';
        case ProductOrderState.SUCCESS:
            return 'success';
        default:
            return 'muted';
    }
}

const VoucherPurchases = observer(() => {
    const { walletClient } = injectTSDI(Api);
    const { formatDate, currentLocale } = injectTSDI(I18n);

    const [statusFilter, setStatusFilter] = React.useState<StatusFilter>(
        ProductOrderState.PENDING
    );
    const [sourceFilter, setSourceFilter] = React.useState<SourceFilter>('ALL');
    const [bulkModalOpen, setBulkModalOpen] = React.useState(false);
    const [syncingId, setSyncingId] = React.useState<number | null>(null);
    const [retryingId, setRetryingId] = React.useState<number | null>(null);
    const [forceRetryingId, setForceRetryingId] = React.useState<number | null>(
        null
    );
    const [deletingId, setDeletingId] = React.useState<number | null>(null);
    const [confirmAction, setConfirmAction] =
        React.useState<ConfirmActionState | null>(null);

    const [searchParams, dispatch] = React.useReducer(useSearchReducer, {
        size: 30,
        page: 0,
        sort: '-createdDate'
    });

    const { setSearchTerm } = userSearchDebounce(dispatch);

    const timeFormatter = React.useMemo(
        () =>
            new Intl.DateTimeFormat(currentLocale, {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
            }),
        [currentLocale]
    );

    const formatTime = React.useCallback(
        (date?: string) => {
            if (!date) {
                return '-';
            }

            const parsedDate = new Date(date);
            if (Number.isNaN(parsedDate.getTime())) {
                return '-';
            }

            return timeFormatter.format(parsedDate);
        },
        [timeFormatter]
    );

    const purchases = useWrapRequest(
        async (searchParams, statusFilter, sourceFilter) => {
            const response =
                await walletClient.supportApi.purchaseSupportControllerGetThirdPartyPurchases(
                    {
                        ...searchParams,
                        ...(statusFilter !== 'ALL' && {
                            statuses: [statusFilter]
                        }),
                        ...(sourceFilter !== 'ALL' && {
                            sources: [sourceFilter]
                        })
                    }
                );

            // Table component uses `id` as a stable row key.
            const content = (response.data.content || []).map((purchase) => ({
                ...purchase,
                id: purchase.purchaseId
            }));

            return { ...response.data, content };
        },
        {
            defaultData: {
                content: [],
                number: 0,
                size: 30,
                totalElements: 0,
                totalPages: 0
            }
        }
    );

    React.useEffect(() => {
        purchases.request([searchParams, statusFilter, sourceFilter]);
    }, [searchParams, statusFilter, sourceFilter]);

    React.useEffect(() => {
        dispatch({ type: 'page', page: 0 });
    }, [statusFilter, sourceFilter]);

    const reload = () => {
        dispatch({ type: 'reload' });
    };

    const handleSync = async (purchaseId: number) => {
        setSyncingId(purchaseId);
        try {
            await walletClient.supportApi.purchaseSupportControllerSyncPurchase(
                purchaseId
            );
            toast.success('Purchase sync initiated');
            reload();
        } catch (e: any) {
            toast.error(getErrorMessage(e));
        } finally {
            setSyncingId(null);
        }
    };

    const openConfirmAction = (
        action: ConfirmActionType,
        purchaseId: number
    ) => {
        setConfirmAction({ action, purchaseId });
    };

    const handleConfirmAction = async () => {
        if (!confirmAction) {
            return false;
        }

        const { action, purchaseId } = confirmAction;

        if (action === 'delete') {
            setDeletingId(purchaseId);
            try {
                await walletClient.supportApi.purchaseSupportControllerDeletePurchase(
                    purchaseId
                );
                toast.success('Purchase deleted');
                setConfirmAction(null);
                reload();
                return true;
            } catch (e: any) {
                toast.error(getErrorMessage(e));
                return false;
            } finally {
                setDeletingId(null);
            }
        }

        const isForceRetry = action === 'forceRetry';

        if (isForceRetry) {
            setForceRetryingId(purchaseId);
        } else {
            setRetryingId(purchaseId);
        }

        try {
            await walletClient.supportApi.purchaseSupportControllerRetryPurchase(
                purchaseId,
                { force: isForceRetry }
            );
            toast.success(
                isForceRetry
                    ? 'Purchase force retry initiated'
                    : 'Purchase retry initiated'
            );
            setConfirmAction(null);
            reload();
            return true;
        } catch (e: any) {
            toast.error(getErrorMessage(e));
            return false;
        } finally {
            setRetryingId(null);
            setForceRetryingId(null);
        }
    };

    const isRowActionLoading = (purchaseId: number) =>
        syncingId === purchaseId ||
        retryingId === purchaseId ||
        forceRetryingId === purchaseId ||
        deletingId === purchaseId;

    return (
        <>
            <PageHeader
                title="Voucher Purchases"
                actions={
                    <>
                        <Button
                            variant="outline"
                            onClick={() => setBulkModalOpen(true)}
                        >
                            <RefreshCw />
                            Re-sync pending
                        </Button>
                        <Button variant="outline" asChild>
                            <Link to={links.purchaseEventDlq()}>
                                <TriangleAlert className="text-warning" />
                                Purchase Event DLQ
                            </Link>
                        </Button>
                    </>
                }
            />

            <div className="mb-4 flex flex-wrap items-end gap-3">
                <Filters<StatusFilter, false>
                    label="Status"
                    value={statusFilter}
                    width={180}
                    options={[
                        { label: 'all', value: 'ALL' },
                        {
                            label: ProductOrderState.PENDING,
                            value: ProductOrderState.PENDING
                        },
                        {
                            label: ProductOrderState.ERROR,
                            value: ProductOrderState.ERROR
                        },
                        {
                            label: ProductOrderState.SUCCESS,
                            value: ProductOrderState.SUCCESS
                        },
                        {
                            label: ProductOrderState.CANCELED,
                            value: ProductOrderState.CANCELED
                        }
                    ]}
                    onChange={(value) => setStatusFilter(value)}
                />

                <Filters<SourceFilter, false>
                    label="Source"
                    value={sourceFilter}
                    width={180}
                    options={[
                        { label: 'all', value: 'ALL' },
                        { label: 'Cadooz', value: VendorSource.CADOOZ },
                        { label: 'GoGift', value: VendorSource.GOGIFT },
                        {
                            label: 'Multivoucher',
                            value: VendorSource.MULTIVOUCHER
                        }
                    ]}
                    onChange={(value) => setSourceFilter(value)}
                />
            </div>

            <TableComponent<ThirdPartyPurchaseSupportDto[]>
                emptyText="No voucher purchases"
                items={purchases}
                columns={[
                    {
                        Header: 'Date',
                        id: 'createdDate',
                        Cell: ({
                            row: { original }
                        }: CellProps<ThirdPartyPurchaseSupportDto>) => (
                            <div>
                                <p className="text-sm">
                                    {formatDate(original.createdDate)}
                                </p>
                                <span className="text-xs text-muted-foreground">
                                    {formatTime(original.createdDate)}
                                </span>
                            </div>
                        ),
                        style: { width: 140 }
                    },
                    {
                        Header: 'Purchase ID',
                        id: 'purchaseId',
                        accessor: (row) => row.purchaseId,
                        style: { width: 110 }
                    },
                    {
                        Header: 'User ID',
                        id: 'userId',
                        Cell: ({
                            row: { original }
                        }: CellProps<ThirdPartyPurchaseSupportDto>) => (
                            <Link to={links.userWallet(original.userId)}>
                                {original.userId}
                            </Link>
                        ),
                        style: { width: 130 }
                    },
                    {
                        Header: 'Vendor',
                        id: 'vendorName',
                        Cell: ({
                            row: { original }
                        }: CellProps<ThirdPartyPurchaseSupportDto>) => (
                            <div>
                                <span className="text-xs text-muted-foreground">
                                    {getThirdPartyType(original)} (
                                    {original.vendorSource})
                                </span>
                                <p className="text-sm">{original.vendorName}</p>
                            </div>
                        )
                    },
                    {
                        Header: 'Variant',
                        id: 'productVariant',
                        accessor: (row) => row.productVariant
                    },
                    {
                        Header: 'Amount',
                        id: 'amount',
                        accessor: (row) =>
                            `${row.amount.value} ${row.amount.currency.symbol}`,
                        style: { width: 140 }
                    },
                    {
                        Header: 'Order',
                        id: 'order',
                        Cell: ({
                            row: { original }
                        }: CellProps<ThirdPartyPurchaseSupportDto>) => (
                            <div>
                                <p className="text-sm">{original.orderId}</p>
                                <span className="text-xs text-muted-foreground">
                                    {original.orderExternalId}
                                </span>
                            </div>
                        ),
                        style: { width: 180 }
                    },
                    {
                        Header: 'Status',
                        id: 'orderStatus',
                        Cell: ({
                            row: { original }
                        }: CellProps<ThirdPartyPurchaseSupportDto>) => {
                            const orderErrorMessage =
                                getOrderErrorMessage(original);

                            return (
                                <div>
                                    <Badge
                                        variant={getOrderStatusVariant(
                                            original.orderStatus
                                        )}
                                    >
                                        {original.orderStatus}
                                    </Badge>
                                    {original.orderStatus ===
                                        ProductOrderState.ERROR &&
                                        orderErrorMessage && (
                                            <p className="mt-1 text-xs text-destructive">
                                                {orderErrorMessage}
                                            </p>
                                        )}
                                </div>
                            );
                        },
                        style: { width: 220 }
                    },
                    {
                        Header: 'Actions',
                        id: 'actions',
                        Cell: ({
                            row: { original }
                        }: CellProps<ThirdPartyPurchaseSupportDto>) => (
                            <div className="flex gap-1">
                                {original.canSync && (
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        className="text-info"
                                        loading={
                                            syncingId === original.purchaseId
                                        }
                                        disabled={isRowActionLoading(
                                            original.purchaseId
                                        )}
                                        onClick={() =>
                                            void handleSync(original.purchaseId)
                                        }
                                    >
                                        <RefreshCw />
                                        Sync
                                    </Button>
                                )}
                                {original.orderStatus ===
                                    ProductOrderState.PENDING && (
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        className="text-warning"
                                        loading={
                                            forceRetryingId ===
                                            original.purchaseId
                                        }
                                        disabled={isRowActionLoading(
                                            original.purchaseId
                                        )}
                                        onClick={() =>
                                            openConfirmAction(
                                                'forceRetry',
                                                original.purchaseId
                                            )
                                        }
                                    >
                                        <RefreshCw />
                                        Force retry
                                    </Button>
                                )}
                                {original.orderStatus ===
                                    ProductOrderState.PENDING && (
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        className="text-destructive"
                                        loading={
                                            deletingId === original.purchaseId
                                        }
                                        disabled={isRowActionLoading(
                                            original.purchaseId
                                        )}
                                        onClick={() =>
                                            openConfirmAction(
                                                'delete',
                                                original.purchaseId
                                            )
                                        }
                                    >
                                        <Trash2 />
                                        Delete
                                    </Button>
                                )}
                                {original.canRetry && (
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        className="text-warning"
                                        loading={
                                            retryingId === original.purchaseId
                                        }
                                        disabled={isRowActionLoading(
                                            original.purchaseId
                                        )}
                                        onClick={() =>
                                            openConfirmAction(
                                                'retry',
                                                original.purchaseId
                                            )
                                        }
                                    >
                                        <RefreshCw />
                                        Retry
                                    </Button>
                                )}
                                {!original.canSync &&
                                    original.orderStatus !==
                                        ProductOrderState.PENDING &&
                                    !original.canRetry && (
                                        <span className="text-xs text-muted-foreground">
                                            -
                                        </span>
                                    )}
                            </div>
                        ),
                        style: { width: 320 }
                    }
                ]}
                onPageSizeChange={(size) => dispatch({ type: 'size', size })}
                onChangePage={(page) => void dispatch({ type: 'page', page })}
                onSearchChange={(value) => setSearchTerm(value)}
                sorting={{ id: 'createdDate', desc: true }}
            />

            <BulkSyncModal
                open={bulkModalOpen}
                sourceFilter={sourceFilter}
                search={searchParams.search}
                onClose={() => setBulkModalOpen(false)}
                onSuccess={() => reload()}
            />

            <ConfirmationModal
                open={Boolean(confirmAction)}
                title={
                    confirmAction?.action === 'delete'
                        ? 'Confirm Delete'
                        : confirmAction?.action === 'forceRetry'
                          ? 'Confirm Force Retry'
                          : 'Confirm Retry'
                }
                buttonText={
                    confirmAction?.action === 'delete'
                        ? 'Delete'
                        : confirmAction?.action === 'forceRetry'
                          ? 'Force retry'
                          : 'Retry'
                }
                destructive={confirmAction?.action === 'delete'}
                submitHandler={handleConfirmAction}
                onModalClose={() => setConfirmAction(null)}
            >
                {confirmAction?.action === 'delete' ? (
                    <>
                        <Alert variant="warning">
                            <AlertDescription>
                                This does not refund the user — no money is
                                returned to their wallet.
                            </AlertDescription>
                        </Alert>
                        <p className="text-sm">
                            The purchase will be hidden from this list. Use only
                            for stuck or abandoned pending orders.
                        </p>
                    </>
                ) : confirmAction?.action === 'forceRetry' ? (
                    <>
                        <Alert variant="warning">
                            <AlertDescription>
                                This is not a refund — no money is returned to
                                the user.
                            </AlertDescription>
                        </Alert>
                        <p className="text-sm">
                            This forces a new order attempt at the vendor even
                            though the current order is still pending. If the
                            pending order also completes, we may be charged
                            twice. Use only when Sync is not enough.
                        </p>
                    </>
                ) : (
                    <p className="text-sm">
                        This files a new order attempt at the third-party vendor
                        for this failed order. No money is refunded to the user.
                        We may be charged for the additional attempt.
                    </p>
                )}
            </ConfirmationModal>
        </>
    );
});

export default VoucherPurchases;
